import React, { useEffect, useState, useContext } from 'react';

import {
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { identity, startCase } from 'lodash';

import { OverlayLoading } from 'components/overlay-loading';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';
import { DisplayImage } from 'components/displayImage';

import { ProfileContext } from 'contexts/profile';
import InspectionService from 'services/inspection';
import { errHandling } from 'utils';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const FIELDS = {
  ASK_BY: 'ask_by',
  DAMAGE_PENALTY_ID: 'damage_penalty_id',
  IMAGES: 'images',
  NOTES: 'notes',
  PRICE: 'price',
  TITLE: 'title',
};

const ONGOING_INSPECTION = 'ongoing_inspection';

export function InspectionCreateEdit({
  closeModal = identity,
  data: { inspection = {} } = {},
  pageStatus,
}) {
  const [form] = Form.useForm();
  const { Profile } = useContext(ProfileContext);
  const [alertImage, setAlertImage] = useState(false);
  const [damageList, setDamageList] = useState([]);
  const [, setHasOtherDamage] = useState(false);
  const [insepctionImgSrc, setInspectionImgSrc] = useState('');
  const [listImageURL, setListImageURL] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [showFullInspectionImg, setShowFullInspectionImg] = useState(false);
  const [value, setValue] = useState({});

  const { first_name, last_name } = Profile;
  const { id: inspectionId, order_product_inspection_id } = inspection;

  const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 16, offset: 2 },
    labelAlign: 'left',
  };

  async function onFinish(values) {
    if (!listImageURL.length) return setAlertImage(true);

    setLoadingButton(true);
    try {
      const payload = {
        penalty: {
          ...values,
          images: listImageURL,
          order_product_inspection_id,
        },
      };
      if (!inspectionId) {
        await InspectionService.createPenalty(payload);
      } else {
        await InspectionService.updatePenalty(value.id, payload);
      }
      message.success('Successfully add a penalty');
      closeModal();
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  async function fetchDamagePenaltyList() {
    setLoadingPage(true);
    try {
      const {
        data: { data },
      } = await InspectionService.getDamagePenaltyList({
        product_id: inspection.product_id,
      });
      setDamageList(data);
      const { ask_by, notes, damage_penalty = {}, images, id } = inspection;
      const { id: damage_penalty_id, title, description } = damage_penalty;
      const adminFullName = [first_name, last_name]
        .filter((val) => val)
        .join(' ');
      setValue({
        ask_by: ask_by || adminFullName,
        damage_penalty_id,
        description,
        id,
        images,
        notes,
        title,
      });
      if (inspectionId) {
        setListImageURL(images);
      }
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    fetchDamagePenaltyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listImageURL.length) setAlertImage(false);
  }, [listImageURL.length]);

  function deleteImage(urlIdx) {
    setListImageURL((prevList) => prevList.filter((_, idx) => idx !== urlIdx));
  }

  const handleOnChangeDamage = (value) => {
    const otherDamage =
      damageList.find(
        ({ title = '' } = {}) => title.toLowerCase() === 'other'
      ) || {};
    setHasOtherDamage(otherDamage.id === value);
  };

  function handleOnClickInspectionImg(imgSrc) {
    setInspectionImgSrc(imgSrc);
    setShowFullInspectionImg(true);
  }

  function handleOnCloseInspectionImg() {
    setInspectionImgSrc('');
    setShowFullInspectionImg(false);
  }

  return (
    <Content>
      <DisplayImage
        imageSrc={insepctionImgSrc}
        onClose={handleOnCloseInspectionImg}
        show={showFullInspectionImg}
      />
      {loadingPage && <OverlayLoading open={loadingPage} />}
      <Form
        className="pd-checking-inspection-form"
        form={form}
        onFinish={onFinish}
        initialValues={{
          [FIELDS.ASK_BY]: value[FIELDS.ASK_BY],
          [FIELDS.DAMAGE_PENALTY_ID]: value[FIELDS.DAMAGE_PENALTY_ID],
          [FIELDS.NOTES]: value[FIELDS.NOTES],
          [FIELDS.IMAGES]: value[FIELDS.IMAGES],
        }}>
        <div className="section-container">
          <Row className="pd-margin-bottom-lg" type="flex" justify="center">
            <Title level={4}>
              {inspectionId ? 'Detail Inspection Result' : 'Add Inspection'}
            </Title>
          </Row>
          <div className="inner-section">
            <Form.Item
              label="Ask by"
              name={FIELDS.ASK_BY}
              {...formItemLayout}
              rules={[
                {
                  required: true,
                  message: 'Please input inspector name!',
                },
              ]}>
              <Input disabled placeholder="Input inspector name" />
            </Form.Item>

            <Form.Item
              label="Damage"
              name={FIELDS.DAMAGE_PENALTY_ID}
              {...formItemLayout}
              rules={[
                {
                  required: true,
                  message: 'Please input your damage!',
                },
              ]}>
              <Select
                disabled={pageStatus !== ONGOING_INSPECTION}
                placeholder="Select damage type"
                onChange={handleOnChangeDamage}>
                {damageList.map(({ id, title } = {}) => (
                  <Option key={id} value={id}>
                    {startCase(title)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Notes"
              name={FIELDS.NOTES}
              {...formItemLayout}
              rules={[
                {
                  required: true,
                  message: 'Please input the notes!',
                },
              ]}>
              <TextArea
                disabled={pageStatus !== ONGOING_INSPECTION}
                placeholder="Write your notes"
              />
            </Form.Item>

            <Form.Item label="Upload Image" {...formItemLayout}>
              <Row gutter={8} className="pd-margin-bottom-md">
                {listImageURL.map((imgSrc, idx) => (
                  <Col className="pd-inspection-upload-img" span={8} key={idx}>
                    {pageStatus === ONGOING_INSPECTION && (
                      <CloseOutlined
                        onClick={deleteImage.bind(this, idx)}
                        className="close-icon"
                        type="close"
                      />
                    )}
                    <img
                      height={100}
                      onClick={handleOnClickInspectionImg.bind(this, imgSrc)}
                      src={imgSrc}
                      width={100}
                      alt="inspection"
                    />
                  </Col>
                ))}
              </Row>
              {alertImage && (
                <Row className="pd-margin-bottom-xs pd-color-red">
                  <Col>Please upload the image</Col>
                </Row>
              )}
              <Form.Item name={FIELDS.IMAGES}>
                <PdUploader
                  afterUpload={(url) =>
                    setListImageURL((prevImageList) => [...prevImageList, url])
                  }
                  disabled={
                    listImageURL.length >= 3 ||
                    pageStatus !== ONGOING_INSPECTION
                  }
                  imgClassName="upload-image"
                />
              </Form.Item>
            </Form.Item>
          </div>
        </div>

        {pageStatus === ONGOING_INSPECTION && (
          <div className="button-container">
            <Form.Item>
              <Row type="flex" justify="center">
                <PdButton
                  className="w-half height-32"
                  full
                  htmlType="submit"
                  loading={loadingButton}
                  type="primary">
                  Save
                </PdButton>
              </Row>
            </Form.Item>
          </div>
        )}
      </Form>
    </Content>
  );
}
